import { useQuery } from "@tanstack/react-query";

import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router";

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { useMemo } from "react";

import type { InferTransformedProcedureOutput } from "@goose/api";
import type { OpportunityId, OrganizationId } from "@goose/schema/Salesforce";
import { UUID } from "@goose/utils/uuid";

import { DataTable } from "~/components/DataTable";
import { QueryState } from "~/components/QueryState";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { Spinner } from "~/components/ui/Spinner";
import { formatDate } from "~/lib/format";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute(
  "/_authenticated/admin/organizations/$organizationId/opportunities/$opportunityId/requirements",
)({
  component: OpportunityRequirements,
});

function OpportunityRequirements() {
  const trpc = useTRPC();
  const { organizationId, opportunityId } = Route.useParams();

  const requirementSets = useQuery(
    trpc.opportunities.requirements.list.queryOptions({
      organizationId,
      opportunityId,
    }),
  );

  return (
    <QueryState
      result={requirementSets}
      loading={
        <div className="flex justify-center items-center py-24">
          <Spinner size="lg" />
        </div>
      }
      error={(error) => <TRPCClientErrorDisplay error={error} />}
    >
      {(data) => (
        <>
          <RequirementSetsTable
            organizationId={organizationId}
            opportunityId={opportunityId}
            data={data}
          />
          <Outlet />
        </>
      )}
    </QueryState>
  );
}

type RequirementSetsList =
  InferTransformedProcedureOutput<"opportunities.requirements.list">;

interface RequirementSetsTableProps {
  organizationId: OrganizationId;
  opportunityId: OpportunityId;
  data: RequirementSetsList;
}

function RequirementSetsTable({
  organizationId,
  opportunityId,
  data,
}: RequirementSetsTableProps) {
  const navigate = useNavigate();

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<RequirementSetsList[number]>();
    return [
      columnHelper.accessor("createdAt", {
        header: "Created",
        cell: (ctx) => (
          <Link
            className="font-medium text-slate-900 underline transition-colors hover:text-slate-500"
            to="/admin/organizations/$organizationId/opportunities/$opportunityId/requirements/$requirementsId"
            params={{
              organizationId,
              opportunityId,
              requirementsId: UUID.fromString(ctx.row.original.id),
            }}
          >
            {formatDate(ctx.getValue())}
          </Link>
        ),
      }),
      columnHelper.accessor(
        (row) =>
          [row.createdBy.name, row.createdBy.email]
            .filter((part) => part != null && part.trim() !== "")
            .join(" "),
        {
          id: "createdBy",
          header: "Created By",
          cell: (ctx) => (
            <Link
              className="font-medium text-slate-900 underline transition-colors hover:text-slate-500"
              to="/admin/organizations/$organizationId/users/$userId"
              params={{
                organizationId: ctx.row.original.organizationId,
                userId: ctx.row.original.createdBy.id,
              }}
            >
              {ctx.row.original.createdBy.name ??
                ctx.row.original.createdBy.email}
            </Link>
          ),
        },
      ),
      columnHelper.accessor("modification", {
        header: "Modification",
        cell: (ctx) => ctx.getValue() ?? "—",
      }),
    ];
  }, [organizationId, opportunityId]);

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    initialState: {
      sorting: [{ id: "createdAt", desc: true }],
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <DataTable
      title="Requirement sets"
      emptyMessage="No requirement sets found"
      table={table}
      onRowClick={(row) => {
        void navigate({
          to: "/admin/organizations/$organizationId/opportunities/$opportunityId/requirements/$requirementsId",
          params: {
            organizationId,
            opportunityId,
            requirementsId: UUID.fromString(row.original.id),
          },
        });
      }}
    />
  );
}
